<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import date from "@/libs/date.ts";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";
import type { Dayjs } from "dayjs";

type Day = {
  date: Dayjs;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
};

const emit = defineEmits(["changed"]);

const props = defineProps<{ currentDate: Dayjs }>();

const month = ref(date(props.currentDate).startOf("month"));

// The parent hydrates the transaction asynchronously, so the prop can change after mount.
// Guarded because some parents pass a freshly built dayjs on every render.
watch(
  () => props.currentDate,
  (currentDate) => {
    if (!currentDate.isSame(month.value, "month")) {
      month.value = currentDate.startOf("month");
    }
  },
);

const calendar = computed(() => {
  const firstOfMonth = month.value.date(1);
  const lastOfMonth = month.value.endOf("month");
  // The header is Monday-first, so the grid has to start on a Monday too: dayjs `get("day")` is
  // Sunday-based, which put every day one column to the left of its label.
  const endOfCalendar = lastOfMonth.add((7 - lastOfMonth.get("day")) % 7, "days");

  let currentDate = firstOfMonth.subtract((firstOfMonth.get("day") + 6) % 7, "days");

  const dates: Day[] = [];

  while (currentDate.isSameOrBefore(endOfCalendar)) {
    dates.push({
      date: currentDate,
      isCurrentMonth:
        currentDate.isSameOrAfter(firstOfMonth) && currentDate.isSameOrBefore(lastOfMonth),
      isToday: currentDate.isSame(date(), "day"),
      // `currentDate` is a calendar day, but the API and the recurrence form hand us values
      // carrying a time (UTC midnight, `new Date()`), so compare days, never milliseconds.
      isSelected: currentDate.isSame(props.currentDate, "day"),
    });

    currentDate = currentDate.add(1, "day");
  }

  return dates;
});

const onPrevious = () => {
  month.value = month.value.subtract(1, "month");
};

const onNext = () => {
  month.value = month.value.add(1, "month");
};

const onSelect = (day: Day) => {
  emit("changed", date(day.date));
};
</script>

<template>
  <div class="hidden w-1/2 max-w-md md:block">
    <div class="flex items-center text-center text-gray-900">
      <button
        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
        type="button"
        @click="onPrevious"
      >
        <span class="sr-only">Vorige maand</span>
        <ChevronLeftIcon aria-hidden="true" class="size-5" />
      </button>
      <div class="flex-auto text-sm font-semibold">
        {{ month.format("MMMM YYYY").toLocaleUpperCase() }}
      </div>
      <button
        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
        type="button"
        @click="onNext"
      >
        <span class="sr-only">Volgende maand</span>
        <ChevronRightIcon aria-hidden="true" class="size-5" />
      </button>
    </div>
    <div class="mt-6 grid grid-cols-7 text-center text-xs/6 text-gray-500">
      <div>M</div>
      <div>T</div>
      <div>W</div>
      <div>T</div>
      <div>F</div>
      <div>S</div>
      <div>S</div>
    </div>
    <div
      class="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200"
    >
      <button
        v-for="(day, dayIdx) in calendar"
        :key="day.date.format('YYYY-MM-DD')"
        :class="[
          'py-1.5 hover:bg-gray-100 focus:z-10',
          day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
          (day.isSelected || day.isToday) && 'font-semibold',
          day.isSelected && 'text-white',
          !day.isSelected && day.isCurrentMonth && !day.isToday && 'text-gray-900',
          !day.isSelected && !day.isCurrentMonth && !day.isToday && 'text-gray-400',
          day.isToday && !day.isSelected && 'text-indigo-600',
          dayIdx === 0 && 'rounded-tl-lg',
          dayIdx === 6 && 'rounded-tr-lg',
          dayIdx === calendar.length - 7 && 'rounded-bl-lg',
          dayIdx === calendar.length - 1 && 'rounded-br-lg',
          'cursor-pointer',
        ]"
        type="button"
        @click="onSelect(day)"
      >
        <time
          :class="[
            'mx-auto flex size-7 items-center justify-center rounded-full',
            day.isSelected && day.isToday && 'bg-indigo-600',
            day.isSelected && !day.isToday && 'bg-gray-900',
          ]"
          :datetime="day.date.format()"
        >
          {{ day.date.date() }}
        </time>
      </button>
    </div>
  </div>
</template>

<style scoped></style>
